import { ImageIcon } from 'lucide-react';
import { useState } from 'react';
import { useI18n } from '@/lib/i18n';
import type { ImageRef } from '@/lib/types';
import { cn } from '@/lib/utils';

/** Builds a srcset for locally stored images that have -thumb / -md variants. */
export function srcSetFor(url?: string) {
  if (!url || !/\/media\/.+\.webp$/.test(url) || /-(thumb|md)\.webp$/.test(url)) return undefined;
  return `${url.replace(/\.webp$/, '-thumb.webp')} 480w, ${url.replace(/\.webp$/, '-md.webp')} 1200w, ${url} 2400w`;
}

/**
 * Responsive, lazy image honouring admin-set focal points. Falls back to a branded
 * placeholder when no image is available.
 */
export function SmartImage({ image, className, sizes = '(max-width: 768px) 100vw, 33vw', priority, alt, rounded = true }: { image?: ImageRef | null; className?: string; sizes?: string; priority?: boolean; alt?: string; rounded?: boolean }) {
  const { L } = useI18n();
  const [failed, setFailed] = useState(false);
  if (!image?.url || failed) {
    return (
      <div className={cn('flex items-center justify-center bg-gradient-to-br from-brand-50 to-forest-50 text-brand-300', rounded && 'rounded-xl', className)} aria-hidden>
        <ImageIcon className="size-8" />
      </div>
    );
  }
  return (
    <img
      src={image.url}
      srcSet={srcSetFor(image.url)}
      sizes={sizes}
      alt={alt ?? L(image.alt) ?? ''}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      fetchPriority={priority ? 'high' : undefined}
      width={image.width}
      height={image.height}
      onError={() => setFailed(true)}
      className={cn('object-cover', rounded && 'rounded-xl', className)}
      style={{ objectPosition: `${image.focalX ?? 50}% ${image.focalY ?? 50}%` }}
    />
  );
}
